import { refreshUser } from '@tshared/auth/token-manager'
import { config } from '@tshared/config/config'
import type { User } from '@tshared/models'
import { expireUserSession, useUserStore } from '@tshared/stores/user-session.store'
import ky from 'ky'

let refreshRequest: Promise<User | null> | null = null

function applyAuthHeaders(request: Request) {
  const token = useUserStore.getState().user?.accessToken

  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`)
  } else {
    request.headers.delete('Authorization')
  }

  request.headers.set('x-ext-authz-sso-token-subdomain', config.getHeaderHost())
}

export const req = ky.create({
  timeout: 20000,
  hooks: {
    beforeRequest: [
      ({ request }) => {
        applyAuthHeaders(request)
      },
    ],

    afterResponse: [
      async ({ request, options, response }) => {
        // not 401, return response
        if (response.status !== 401) {
          return response
        }

        // avoid refresh token endpoint self-loop
        if (request.url.includes('refresh-token') || request.url.includes('/mobile/auth/refresh')) {
          return response
        }

        // multiple requests share a refresh promise
        refreshRequest ??= refreshUser(true)

        // Let transient refresh failures propagate instead of turning them
        // back into the original 401 response.
        try {
          const user = await refreshRequest

          // refresh failed
          if (!user?.accessToken) {
            return response
          }
        } finally {
          refreshRequest = null
        }

        // clone original request
        const newRequest = request.clone()

        // write new token
        applyAuthHeaders(newRequest)

        // Replay exactly once with the refreshed token. This standalone Ky
        // invocation intentionally has no refresh hook, preventing a loop.
        const replayResponse = await ky(newRequest, {
          ...options,
          throwHttpErrors: false,
        })

        if (replayResponse.status === 401) {
          await expireUserSession()
        }

        return replayResponse
      },
    ],
  },
})

export default req
